/**
 * Gmail-style relative date for conversation list rows.
 * - Today: time only (e.g. `3 PM`)
 * - Yesterday: `Yesterday 2 PM`
 * - Earlier this year: `May 26`
 * - Other years: `2024-12-05`
 */
export function formatConversationListDate(
  iso: string | undefined | null,
  now: Date = new Date(),
): string {
  if (iso == null || String(iso).trim() === '') {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }

  const startOfDay = (date: Date): Date => {
    const x = new Date(date);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const today = startOfDay(now);
  const thatDay = startOfDay(d);
  const dayDiff = Math.floor((today.getTime() - thatDay.getTime()) / 86_400_000);
  const timeStr = formatShortClockTime(d);

  if (dayDiff === 0) {
    return timeStr;
  }
  if (dayDiff === 1) {
    return `Yesterday ${timeStr}`;
  }
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatShortClockTime(d: Date): string {
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) {
    hours = 12;
  }
  if (minutes === 0) {
    return `${hours} ${ampm}`;
  }
  const mm = String(minutes).padStart(2, '0');
  return `${hours}:${mm} ${ampm}`;
}

/**
 * Thread message timestamp — always includes clock time (e.g. `May 19 3 PM`, `Yesterday 2 PM`).
 * Sidebar list rows keep {@link formatConversationListDate} (date-only for older this-year items).
 */
export function formatMessageThreadTimestamp(
  iso: string | undefined | null,
  now: Date = new Date(),
): string {
  if (iso == null || String(iso).trim() === '') {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }

  const startOfDay = (date: Date): Date => {
    const x = new Date(date);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const today = startOfDay(now);
  const thatDay = startOfDay(d);
  const dayDiff = Math.floor((today.getTime() - thatDay.getTime()) / 86_400_000);
  const timeStr = formatShortClockTime(d);

  if (dayDiff === 0) {
    return timeStr;
  }
  if (dayDiff === 1) {
    return `Yesterday ${timeStr}`;
  }
  if (d.getFullYear() === now.getFullYear()) {
    const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${datePart} ${timeStr}`;
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day} ${timeStr}`;
}
